import * as React from 'react';
import * as ReactDOM from 'react-dom';
import clsx from 'clsx';

import ReactPlayer from 'react-player/lazy';
import { CSSTransition } from 'react-transition-group';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as style from './videoOverlay.module.scss';

export type Props = {
  title: string;
  url: string;
  open: boolean;
  setOpen: (boolean: boolean) => void;
  type?: string;
  muted?: boolean;
};

const MILESTONES = [10, 25, 50, 75, 100];

export const VideoOverlay: React.FC<Props> = ({
  open,
  title,
  url,
  setOpen,
  type,
  muted = false,
}) => {
  // state
  const [mounted, setMounted] = React.useState(false);

  // refs
  const videoOverlayRef = React.useRef<HTMLDialogElement | null>(null);
  const playerRef = React.useRef<ReactPlayer | null>(null);

  // Track analytics state
  const startSentRef = React.useRef(false);
  const lastPercentRef = React.useRef(-1);
  const sentMilestonesRef = React.useRef<number[]>([]);

  // mount
  React.useEffect(() => {
    setMounted(true);
  }, []);

  // body overflow
  React.useEffect(() => {
    document.body.classList.toggle('body-overflow-hidden', open);
  }, [open]);

  // reset tracking when overlay opens or URL changes
  React.useEffect(() => {
    if (open) {
      startSentRef.current = false;
      lastPercentRef.current = 0;
      sentMilestonesRef.current = [];
    }
  }, [open, url]);

  // Existing GA_TAG_EVENT helper. Do not remove.
  const sendEvent = (action: string) => {
    const isBrowser = typeof window !== 'undefined';
    if (isBrowser) {
      window.dataLayer.push({
        event: 'gtm.custom_event',
        eventInfo: {
          title: title,
          url: url,
          category: 'Video',
          action: action,
        },
      });
    }
  };

  /** Push a video tracking event to the dataLayer. */
  const pushVideoEvent = (event: string, watched?: string) => {
    if (typeof window === 'undefined') return;
    const payload: Record<string, unknown> = {
      event,
      video_title: title,
    };
    if (watched) payload.watched = watched;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  };

  /** Detect milestone crossings without sending events yet. */
  const handleProgress = ({ played }: { played: number }) => {
    const percent = played * 100;
    if (!startSentRef.current && percent > 0) {
      pushVideoEvent('video_start_custom');
      startSentRef.current = true;
    }
    if (percent > lastPercentRef.current) {
      lastPercentRef.current = Math.min(percent, 100);
    }
  };

  /** Flush queued milestone events to the dataLayer. */
  const flushProgressEvents = () => {
    const maxPct = lastPercentRef.current;
    MILESTONES.forEach((threshold) => {
      if (
        threshold <= maxPct &&
        !sentMilestonesRef.current.includes(threshold)
      ) {
        pushVideoEvent('video_progress_custom', `${threshold}%`);
        sentMilestonesRef.current.push(threshold);
      }
    });
  };

  /** Handle pause: flush milestones then send GA event. */
  const handlePause = () => {
    flushProgressEvents();
  };

  /** Handle end: ensure 100 % milestone, flush, send completion and GA event. */
  const handleEnded = () => {
    // At video end we know we've hit 100 %, so mark all remaining milestones
    lastPercentRef.current = 100;
    flushProgressEvents();
    pushVideoEvent('video_complete_custom');
    sendEvent('Ended');
  };

  /** Handle clicks outside the modal: flush milestones, send GA pause, then close. */
  const handleOutsideClick = (evt: React.MouseEvent) => {
    if (evt.target === videoOverlayRef.current) {
      flushProgressEvents();
      setOpen(false);
    }
  };

  // ESC key closes modal
  React.useEffect(() => {
    const onEscKey = (event: KeyboardEvent) =>
      event.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onEscKey);
    return () => {
      window.removeEventListener('keydown', onEscKey);
    };
  }, [setOpen]);

  return mounted
    ? ReactDOM.createPortal(
        <CSSTransition unmountOnExit in={open} timeout={300}>
          <dialog
            ref={videoOverlayRef}
            className={clsx(style.wrapper, type === 'large' && 'is_large')}
            tabIndex={-1}
            aria-labelledby={title}
            onClick={handleOutsideClick}>
            <div className={clsx(style.dialog)} role="document">
              <div className={clsx(style.content)}>
                <div className={clsx(style.body)}>
                  <button
                    className={clsx(style.close)}
                    onClick={() => {
                      // Treat closing as a pause: flush milestones, GA pause, then close
                      flushProgressEvents();
                      sendEvent('Pause');
                      setOpen(false);
                    }}>
                    <FontAwesomeIcon
                      className={clsx(style.closeIcon)}
                      icon={['fas', 'times']}
                    />
                  </button>
                  <h5 className={clsx(style.title)}>{title}</h5>
                  <div className={clsx(style.player)}>
                    {url && (
                      <ReactPlayer
                        ref={playerRef}
                        controls
                        playing
                        playsinline
                        url={url.replace(
                          /(https?:\/\/)(www\.)?youtube\.com\/watch\?v=|https?:\/\/youtu\.be\//g,
                          'https://www.youtube-nocookie.com/embed/'
                        )}
                        width="100%"
                        height="auto"
                        muted={muted}
                        config={{
                          youtube: {
                            playerVars: {
                              enablejsapi: 1,
                              noCookie: true,
                            },
                            embedOptions: {
                              host: 'https://www.youtube-nocookie.com',
                            },
                          },
                        }}
                        onPlay={() => {
                          sendEvent('Play');
                        }}
                        onPause={handlePause}
                        onEnded={handleEnded}
                        onProgress={handleProgress}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </dialog>
        </CSSTransition>,
        document.querySelector('#modal-root') as Element
      )
    : null;
};
